import React, { useState } from 'react';
import InputMask from 'react-input-mask';
import {
  TextField, Button, Grid, Typography, Checkbox, FormControlLabel, Container, Paper, MenuItem, Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import Navbar from "../../../../globals/components/Navbar";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { apiClient } from "../../../../globals/utils/api";
import debounce from "lodash.debounce";
import { EMAIL_DEBOUNCE_TIMEOUT } from "../../../../globals/utils/constants";
import { useSelector } from 'react-redux';

const CreateInternalUser = () => {
  const navigate = useNavigate();
  const [permissionLevel, setPermissionLevel] = useState("");
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    lastName: "",
    phoneNumber: "",
  });
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [isPiiUser, setIsPiiUser] = useState(false);
  const [isCriticalUser, setIsCriticalUser] = useState(false);
  const [sendInvite, setSendInvite] = useState(false);

  const userdetails = useSelector((state) => state.auth.user);
  const isCritical = userdetails?.is_critical_user;

  // Debounced function to check email existence
  const debouncedCheckEmail = React.useRef(
    debounce(async (email) => {
      if (!email) {
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        return;
      }
      setEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setEmailDialogOpen(true);
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } finally {
        setEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT)
  ).current;

  const handleCancel = () => {
    navigate(-1); // Go back to the previous page
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === "email") {
      debouncedCheckEmail(value);
    }
  };

  const handlePermissionChange = (event) => {
    const { value, checked } = event.target;
    setPermissions((prevPermissions) =>
      checked ? [...prevPermissions, value] : prevPermissions.filter((perm) => perm !== value)
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const payload = {
      features: permissionLevel === "Admin" ? [] : permissions,
      full_name: formData.firstName + " " + formData.lastName,
      phonenumber: formData.phoneNumber,
      user: {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName
      },
      is_admin: permissionLevel === "Admin",
      is_pii_user: isPiiUser,
      is_critical_user: isCriticalUser,
      send_invite: sendInvite,
    };

    try {
      const response = await apiClient("/invite-internal-user", "POST", payload);
      if (response) {
        toast.success(
          sendInvite
            ? "Internal User created and invite sent successfully"
            : "Internal User created successfully"
        );
      }
      navigate(-1);
    } catch (error) {
      toast.error("Error submitting form");
    } finally {
      setLoading(false);

    }
  };

  const renderLabel = (mainText, description) => (
    <span>
      <Typography component="span" style={{ fontWeight: 'bold' }}>{mainText}</Typography> - {description}
    </span>
  );

  // Dialog handlers
  const handleDialogClose = () => {
    setEmailDialogOpen(false);
  };
  const handleDialogContinue = () => {
    setEmailDialogOpen(false);
    setEmailExists(false);
    // Keep the email as is, allow user to proceed
  };
  const handleDialogCancel = () => {
    setEmailDialogOpen(false);
    setFormData((prev) => ({ ...prev, email: "" }));
    setEmailExists(false);
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" style={{ marginTop: '3rem' }}>
        <Paper elevation={3} style={{ padding: '2rem', backgroundColor: '#f5f5f5', borderRadius: '15px' }}>
          <Typography variant="h4" gutterBottom style={{ textAlign: 'center', fontWeight: 'bold' }}>
            Create New Internal User
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  fullWidth
                  value={formData.email}
                  onChange={handleInputChange}
                  name="email"
                  variant="outlined"
                  required
                  type="email"
                  error={Boolean(emailExists)}
                  helperText={
                    emailChecking
                      ? "Checking email..."
                      : emailExists
                        ? "This email already exists."
                        : ""
                  }
                />
                {/* Email Exists Dialog */}
                <Dialog
                  open={emailDialogOpen}
                  onClose={(_, reason) => {
                    if (reason !== 'backdropClick') handleDialogClose();
                  }}
                >
                  <DialogTitle>Email Already Exists</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      This email already exists. Would you like to continue with this email?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleDialogCancel} color="secondary">Cancel</Button>
                    <Button onClick={handleDialogContinue} color="primary" autoFocus>
                      Continue
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="First Name"
                  fullWidth
                  value={formData.firstName}
                  onChange={handleInputChange}
                  name="firstName"
                  variant="outlined"
                  required
                  type="text"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Last Name"
                  fullWidth
                  value={formData.lastName}
                  onChange={handleInputChange}
                  name="lastName"
                  variant="outlined"
                  required
                  type="text"
                />
              </Grid>
              <Grid item xs={12}>
                <InputMask
                  mask="(999) 999-9999"  // US format; change as needed
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="Phone Number"
                      fullWidth
                      name="phoneNumber"
                      variant="outlined"
                      required
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Permission Level"
                  fullWidth
                  variant="outlined"
                  select
                  required
                  value={permissionLevel}
                  onChange={(e) => setPermissionLevel(e.target.value)}
                >
                  <MenuItem value="User">User</MenuItem>
                  <MenuItem value="Admin">Admin</MenuItem>
                </TextField>
              </Grid>
              {isCritical && (
                <Grid item xs={12}>
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginTop: '1rem' }}>
                    User Access Flags
                  </Typography>

                  <Grid container spacing={2} direction="column" sx={{ mt: 0.5 }}>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isPiiUser}
                            onChange={(e) => setIsPiiUser(e.target.checked)}
                          />
                        }
                        label={renderLabel(
                          "Has PII Access",
                          "Grants access to view sensitive PII fields."
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isCriticalUser}
                            onChange={(e) => setIsCriticalUser(e.target.checked)}
                          />
                        }
                        label={renderLabel(
                          "Critical User",
                          "Gives priveldged access to the portal."
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {permissionLevel === "User" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginTop: '1rem' }}>
                      Account Management Features
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2} direction="column">
                      {[
                        { label: "Internal Users", value: "internal_users" },
                        { label: "Law Firms", value: "law_firms" },
                        { label: "Law Firm User Management", value: "law_firm_users" },
                        { label: "Bond Numbers", value: "bond_numbers" },
                        { label: "Law Schools", value: "law_schools" },
                        { label: "Professional Guardians", value: "professional_guardians" },
                        { label: "Carriers", value: "carriers" },
                        { label: "Premiums", value: "premiums" },
                        { label: "States", value: "states" },
                        { label: "Bond Types", value: "bond_types" },
                        { label: "Obligees", value: "obligees" },
                        { label: "Enhancement Request", value: "enhancement_request" },
                        { label: "Analytics", value: "analytics" },
                        { label: "Bond Archives", value: "bond_archives" },
                        { label: "Historic Bonds Creation", value: "create_historic_bonds" },
                        { label: "Paralegal Management", value: "LEGAL_SERVICE_PARALEGAL_MANAGEMENT" },
                        { label: "Approve Bonds", value: "approve_bonds" },
                        { label: "Announcements", value: "announcements" },
                        { label: "Support Requests", value: "support_requests" },
                      ].map((feature) => (
                        <Grid item key={feature.value}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={permissions.includes(feature.value)}
                                onChange={handlePermissionChange}
                                value={feature.value}
                              />
                            }
                            label={renderLabel(feature.label, `Allows access to manage ${feature.label.toLowerCase()}.`)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </>
              )}


              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendInvite}
                      onChange={(e) => setSendInvite(e.target.checked)}
                    />
                  }
                  label="Send Invite Email"
                />
              </Grid>

              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
                  <CircularProgress />
                </Box>
              )}

              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                <Button variant="contained" color="warning" onClick={handleCancel} style={{ marginRight: '1rem' }}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={loading}>
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>

    </>
  );
};

export default CreateInternalUser;
